import { type ReactNode, useId } from 'react';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import { chipSx, wf } from '../shell/tokens';

import { IntakeJourneyStatus } from './IntakeJourneyStatus';

export type IntakeStatusTab = 'billing' | 'credentialing' | 'ehr-import';

export function CredentialingComingSoon() {
  return (
    <IntakeJourneyStatus title="Credentialing" statusLabel="Coming soon">
      <Typography>Your practice’s credentialing progress will appear here.</Typography>
    </IntakeJourneyStatus>
  );
}

export function EhrImportComingSoon({ action }: { action?: ReactNode }) {
  return (
    <IntakeJourneyStatus title="Import patients" statusLabel="Coming soon" action={action}>
      <Typography>
        Independent of verification and payers. Start it whenever your export is ready. Your first
        claim needs a patient with insurance on file, so this and payer connections both have to
        finish before you can bill.
      </Typography>
    </IntakeJourneyStatus>
  );
}

/** The status shell composes the existing service surfaces without inventing completion state. */
export function IntakeStatusDashboard({
  selectedTab,
  onSelectTab,
  billing,
  ehrImport,
  billingStatus = 'View current status',
  credentialingLockedReason,
}: {
  selectedTab: IntakeStatusTab;
  onSelectTab: (tab: IntakeStatusTab) => void;
  billing: ReactNode;
  ehrImport?: ReactNode;
  billingStatus?: string;
  credentialingLockedReason?: string;
}) {
  const id = useId();
  const tabs = [
    {
      value: 'billing',
      label: 'Billing',
      caption: 'Your payer connections',
      status: billingStatus,
    },
    {
      value: 'credentialing',
      label: 'Credentialing',
      caption: credentialingLockedReason ?? 'Credentialing for your practice',
      status: credentialingLockedReason ? 'Locked' : 'Coming soon',
    },
    {
      value: 'ehr-import',
      label: 'EHR Import',
      caption: 'Bring your patient records',
      status: 'Coming soon',
    },
  ] as const;
  return (
    <Stack spacing={3}>
      <Tabs
        value={selectedTab}
        onChange={(_, value: IntakeStatusTab) => onSelectTab(value)}
        aria-label="Go Live services"
        variant="fullWidth"
        sx={{
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-list': { gap: 1.5 },
          '& .MuiTab-root': { minWidth: 0 },
        }}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            id={`${id}-${tab.value}-tab`}
            aria-controls={`${id}-${tab.value}-panel`}
            sx={{
              border: '2px solid',
              borderColor: selectedTab === tab.value ? wf.blue : wf.line,
              borderRadius: 2,
              bgcolor: wf.surface,
              textAlign: 'left',
              textTransform: 'none',
              alignItems: 'stretch',
              p: 2,
            }}
            label={
              <Stack spacing={1}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 700, color: wf.ink }}>
                    {tab.label}
                  </Typography>
                  <Box component="span" sx={chipSx('mut')}>
                    {tab.status}
                  </Box>
                </Stack>
                <Typography component="span" variant="body2" sx={{ color: wf.muted }}>
                  {tab.caption}
                </Typography>
              </Stack>
            }
          />
        ))}
      </Tabs>
      <Box
        role="tabpanel"
        id={`${id}-${selectedTab}-panel`}
        aria-labelledby={`${id}-${selectedTab}-tab`}
      >
        {selectedTab === 'billing' ? (
          billing
        ) : selectedTab === 'credentialing' ? (
          credentialingLockedReason ? (
            <IntakeJourneyStatus title="Credentialing" statusLabel="Locked">
              <Typography>{credentialingLockedReason}</Typography>
            </IntakeJourneyStatus>
          ) : (
            <CredentialingComingSoon />
          )
        ) : (
          (ehrImport ?? <EhrImportComingSoon />)
        )}
      </Box>
    </Stack>
  );
}
